import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { createSitewizard } from "../services/api";
import LoadingScreen from "../components/Loading";

const CHAT_API_URL = process.env.REACT_APP_NODE_ENV === 'dev' ? "http://localhost:3001/sitegeneration/api/chat" : "/sitegeneration/api/chat";
const BRANDING_PROMPT_ID = "7810f490-a67d-4716-a446-31230ee4ab0e";
const MINT_TOKEN_URL = process.env.REACT_APP_NODE_ENV === 'dev' 
  ? "http://localhost:3001/sitegeneration/api/mint-token" 
  : "/sitegeneration/api/mint-token";

const BRANDING_PROMPT = `You are a senior branding and naming expert.
I will give you a domain name. Based ONLY on the domain (ignore current ownership or existing companies), generate a high-quality brand concept and answer the following three questions clearly and professionally:

1. What's the name of your website or business?
   - Convert the domain into a clean, brand-ready name
   - Use proper capitalization
   - If there are multiple strong interpretations, pick the one that feels most compelling

2. What's your website about?
   - Provide a clear overview of the purpose or focus of the site
   - Who it's for
   - Core offerings, content, or mission
   - This could be a business, a personal site, a hobby project, a portfolio, or anything the domain inspires

3. Describe your brand or site in a few sentences — what makes it unique?
   - Brand personality and tone (e.g., professional, playful, creative, trustworthy, modern, nostalgic)
   - What differentiates it from others in its space
   - Why someone would engage with or visit this site

Guidelines:
- Do NOT reference existing companies unless explicitly asked
- Be confident and decisive — no hedging
- Make the output polished, realistic, and ready to use as a brand concept
- Consider different possibilities the domain suggests, including business, personal, creative, or hobby uses

Domain name: `;

export default function DomainGenerator() {
  const { domain } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [userIp, setUserIp] = useState("");
  const [started, setStarted] = useState(false);

  const domainName = domain || searchParams.get("domain");

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => setUserIp(data.ip))
      .catch(() => setUserIp("127.0.0.1"));
  }, []);

  useEffect(() => {
    if (!domainName) {
      setError("No domain provided");
      return;
    }
    if (userIp && !started) {
      setStarted(true);
      generateSite();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainName, userIp]);

  async function generateSite() {
    try {
      // Step 1: Mint JWT
      const mintResponse = await fetch(MINT_TOKEN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip: userIp }),
      });

      if (!mintResponse.ok) throw new Error("Failed to get token");
      const { token } = await mintResponse.json();

      // Step 2: Call AI API
      const requestBody = {
        PromptId: BRANDING_PROMPT_ID,
        OpenAiChatOptions: {
          messages: [{ role: "user", content: BRANDING_PROMPT + domainName }],
          Model: "netsol-general-gpt4.1-mini",
          Tools: [
            {
              type: "function",
              function: {
                name: "generate_brand_concept",
                description: "Generate a brand concept based on a domain name",
                strict: true,
                parameters: {
                  type: "object",
                  properties: {
                    brand_name: { type: "string" },
                    website_overview: { type: "string" },
                    brand_description: { type: "string" },
                  },
                  required: ["brand_name", "website_overview", "brand_description"],
                  additionalProperties: false,
                },
              },
            },
          ],
        },
      };

      const chatResponse = await fetch(CHAT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Forwarded-For": userIp,
        },
        body: JSON.stringify(requestBody),
      });

      const chatData = await chatResponse.json();
      if (!chatData.success) throw new Error(chatData.errorMessage || "AI request failed");

      // Step 3: Parse response
      const choice = chatData.openAiResponse?.choices?.[0];
      let parsedBranding = null;

      if (choice?.message?.tool_calls?.[0]?.function?.arguments) {
        const args = choice.message.tool_calls[0].function.arguments;
        parsedBranding = typeof args === "string" ? JSON.parse(args) : args;
      } else if (choice?.message?.content) {
        const jsonMatch = choice.message.content.match(/\{[\s\S]*\}/);
        if (jsonMatch) parsedBranding = JSON.parse(jsonMatch[0]);
      }

      if (!parsedBranding) throw new Error("Failed to parse AI response");

      // Step 4: Create site
      const resp = await createSitewizard({
        title: parsedBranding.website_overview,
        unique: parsedBranding.brand_name,
        prompt: parsedBranding.brand_description,
      });

      // Redirect to preview
      navigate("/sitegeneration/preview", {
        state: { previewUrl: resp.previewUrl, project: resp.project },
      });
    } catch (err) {
      setError(err.message || "Something went wrong");
    }
  }

  if (error) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p style={{ color: "red" }}>{error}</p>
      </div>
    );
  }

  return <LoadingScreen />;
}
